import { useRef, useState } from 'react'
import { Ban, ChevronDown, Mail, PencilLine, Truck, UserRoundPen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { InlineFieldInput } from '@/components/inline-field-group'
import { ComposeInput, ComposeTextarea } from '@/components/compose-field'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { cn, formatMoney } from '@/lib/utils'
import type { components } from '@/api/schema'
import {
  isPopoverRecovery,
  type InlineRecoveryMode,
  type RecoveryMode,
  type RecoveryPrefill,
} from './action-recovery-modes'
import { recoveryHandlers } from './action-recovery-popover'
import { useActionItemSource } from './action-source'
import { useReviseLineItemAction, useSendDraftResponse } from '@/features/purchase-orders/hooks'

type PurchaseOrderAction = NonNullable<
  components['schemas']['PurchaseOrderDetailRead']['actions']
>[number]
type LineItems = components['schemas']['PurchaseOrderDetailRead']['line_items']
type LineItem = NonNullable<LineItems>[number]

const MENU_ITEMS: {
  mode: RecoveryMode
  label: string
  icon: typeof PencilLine
}[] = [
  { mode: 'revise', label: 'Revise a line', icon: PencilLine },
  { mode: 'redirect', label: 'Send PO to another contact', icon: UserRoundPen },
  { mode: 'freight', label: 'Accept prepaid & add freight', icon: Truck },
  { mode: 'cancel', label: 'Record cancellation', icon: Ban },
]

export function MoreFixesMenu({
  onChoose,
  onCancelPurchaseOrder,
  disabled,
}: {
  onChoose: (mode: RecoveryMode) => void
  /** Set where the item may be answered by cancelling the PO with its manufacturer (USA-1693). */
  onCancelPurchaseOrder?: () => void
  disabled?: boolean
}) {
  const opensPopover = useRef(false)
  const [open, setOpen] = useState(false)
  // Every fix here changes the purchase order; customer and manufacturer data items have none.
  if (useActionItemSource().kind !== 'purchase_order') return null
  return (
    <PickerMenu open={open} onOpenChange={setOpen}>
      <PickerMenuTrigger asChild>
        <Button size="sm" variant="ghost" disabled={disabled}>
          More fixes <ChevronDown className="size-3.5" aria-hidden="true" />
        </Button>
      </PickerMenuTrigger>
      {/* A popover fix keeps focus in its popover instead of back on this button. */}
      <PickerMenuContent
        title="More fixes"
        titleOnPhoneOnly
        align="end"
        onCloseAutoFocus={(event) => {
          if (opensPopover.current) event.preventDefault()
          opensPopover.current = false
        }}
      >
        <PickerMenuList>
          {onCancelPurchaseOrder && (
            <PickerMenuItem
              onSelect={() => {
                setOpen(false)
                onCancelPurchaseOrder()
              }}
            >
              <Mail className="size-4" aria-hidden="true" />
              Email manufacturer & cancel PO
            </PickerMenuItem>
          )}
          {MENU_ITEMS.map(({ mode, label, icon: Icon }) => (
            <PickerMenuItem
              key={mode}
              onSelect={() => {
                opensPopover.current = isPopoverRecovery(mode)
                setOpen(false)
                onChoose(mode)
              }}
            >
              <Icon className="size-4" aria-hidden="true" />
              {label}
            </PickerMenuItem>
          ))}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}

function parseStructured(action: PurchaseOrderAction): Record<string, unknown> {
  try {
    const parsed = action.structured_data ? JSON.parse(action.structured_data) : null
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

const DEFAULT_REDIRECT_BODY =
  'Hello,\n\nPlease find our purchase order attached. Kindly confirm receipt and the expected ship date.\n\nThank you.'

function LineTerm({
  label,
  value,
  strong = false,
}: {
  label: string
  value: string | number
  strong?: boolean
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={strong ? 'font-medium' : undefined}>{value}</dd>
    </div>
  )
}

function LineIdentity({ line, compact = false }: { line: LineItem; compact?: boolean }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <POLineItemProductCell
        model={line.model}
        description={line.description}
        productUuid={line.product?.uuid}
        imageUrl={line.product?.image_url}
        manufacturerName={line.manufacturer_name || line.product?.manufacturer_name}
        manufacturerUuid={line.product?.manufacturer_uuid}
        showManufacturer={false}
        compact={compact}
        collapseLongDescription={!compact}
        imageClassName="h-12 w-12"
        className="flex-1"
      />
      <dl className="flex shrink-0 gap-4 text-xs tabular-nums sm:text-right">
        <LineTerm label="Qty" value={line.quantity} />
        <LineTerm label="Net" value={formatMoney(line.unit_price)} />
        {line.discount_display && <LineTerm label="Discount" value={line.discount_display} />}
        <LineTerm label="Total" value={formatMoney(line.line_total)} strong />
      </dl>
    </div>
  )
}

export function ActionRecoveryForm({
  mode,
  action,
  poId,
  lineItems,
  prefill,
  onClose,
}: {
  mode: InlineRecoveryMode
  action: PurchaseOrderAction
  poId: string
  lineItems: LineItems
  prefill?: RecoveryPrefill
  onClose: () => void
}) {
  const structured = parseStructured(action)
  const productLines = (lineItems ?? []).filter(
    (line) => line.line_kind !== 'fee' && line.line_kind !== 'accessorial',
  )
  const [lineItemId, setLineItemId] = useState<number | null>(
    action.line_item_id ?? (productLines.length === 1 ? (productLines[0]?.id ?? null) : null),
  )
  const [quantity, setQuantity] = useState(
    typeof structured.suggested_quantity === 'number' ? String(structured.suggested_quantity) : '',
  )
  const suggestedChain =
    typeof structured.corrected_discount_chain === 'string'
      ? structured.corrected_discount_chain
      : ''
  const [discountChain, setDiscountChain] = useState(suggestedChain)
  const [unitPrice, setUnitPrice] = useState(
    !suggestedChain && action.action_type === 'price_change' && structured.suggested_price != null
      ? String(structured.suggested_price)
      : '',
  )
  const [recipientEmail, setRecipientEmail] = useState(prefill?.recipientEmail ?? '')
  const [recipientName, setRecipientName] = useState(prefill?.recipientName ?? '')
  // Who else gets this email (USA-1663).
  const [ccEmails, setCcEmails] = useState('')
  const [replyBody, setReplyBody] = useState(prefill?.replyBody || DEFAULT_REDIRECT_BODY)

  const revise = useReviseLineItemAction(poId)
  const redirect = useSendDraftResponse(poId)
  const pending = revise.isPending || redirect.isPending
  const handlers = (success: string, failure: string) => recoveryHandlers(success, failure, onClose)

  const back = (
    <Button size="sm" variant="ghost" onClick={onClose} disabled={pending}>
      Back
    </Button>
  )
  const fieldId = (name: string) => `recovery-${name}-${action.id}`

  if (mode === 'revise') {
    const selected = productLines.find((line) => line.id === lineItemId)
    const parsedQuantity = quantity ? Number(quantity) : undefined
    const parsedPrice = unitPrice ? Number(unitPrice) : undefined
    const quantityValid =
      parsedQuantity === undefined || (Number.isInteger(parsedQuantity) && parsedQuantity >= 1)
    const priceValid = parsedPrice === undefined || parsedPrice >= 0
    const hasChange =
      (parsedQuantity !== undefined && parsedQuantity !== selected?.quantity) ||
      parsedPrice !== undefined ||
      discountChain.trim() !== ''
    return (
      <div className="space-y-3 rounded-md border bg-muted/30 p-3">
        <p className="text-sm font-medium">Revise a line</p>
        {!action.line_item_id && productLines.length > 1 ? (
          <div className="space-y-2" role="radiogroup" aria-label="Line item">
            {productLines.map((line) => (
              <button
                key={line.id}
                type="button"
                role="radio"
                aria-checked={lineItemId === line.id}
                onClick={() => setLineItemId(line.id)}
                className={cn(
                  'w-full rounded-md border bg-background p-3 text-left transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  lineItemId === line.id && 'border-primary ring-1 ring-primary',
                )}
              >
                <LineIdentity line={line} compact />
              </button>
            ))}
          </div>
        ) : (
          selected && (
            <div className="rounded-md border bg-background p-3">
              <LineIdentity line={selected} />
            </div>
          )
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor={fieldId('quantity')} className="text-xs text-muted-foreground">
              Quantity
            </Label>
            <InlineFieldInput
              id={fieldId('quantity')}
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              placeholder={selected ? String(selected.quantity) : ''}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={fieldId('net')} className="text-xs text-muted-foreground">
              Net price ($)
            </Label>
            <InlineFieldInput
              id={fieldId('net')}
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={unitPrice}
              disabled={discountChain.trim() !== ''}
              onChange={(event) => setUnitPrice(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={fieldId('discount')} className="text-xs text-muted-foreground">
              Discount
            </Label>
            <InlineFieldInput
              id={fieldId('discount')}
              placeholder="e.g. 50/10"
              value={discountChain}
              disabled={unitPrice !== ''}
              onChange={(event) => setDiscountChain(event.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <LoadingButton
            size="sm"
            loading={revise.isPending}
            loadingText="Revising..."
            disabled={!selected || !hasChange || !quantityValid || !priceValid || pending}
            onClick={() =>
              revise.mutate(
                {
                  actionId: action.id,
                  lineItemId: lineItemId ?? undefined,
                  quantity: parsedQuantity,
                  unitPrice: parsedPrice,
                  discountChain: discountChain.trim() || undefined,
                },
                handlers('Line revised and PO updated', 'Line not revised'),
              )
            }
          >
            Revise line
          </LoadingButton>
          {back}
        </div>
      </div>
    )
  }

  // Redirect: send the PO to a corrected contact.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())
  const cc = ccEmails.split(/[,;\s]+/).map((address) => address.trim()).filter(Boolean)
  const ccValid = cc.every((address) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address))
  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <p className="text-sm font-medium">Send PO to another contact</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={fieldId('email')} className="text-xs text-muted-foreground">
            Email
          </Label>
          <ComposeInput
            id={fieldId('email')}
            type="email"
            autoComplete="off"
            value={recipientEmail}
            onChange={(event) => setRecipientEmail(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={fieldId('name')} className="text-xs text-muted-foreground">
            Name
          </Label>
          <ComposeInput
            id={fieldId('name')}
            autoComplete="off"
            value={recipientName}
            onChange={(event) => setRecipientName(event.target.value)}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor={fieldId('cc')} className="text-xs text-muted-foreground">
            CC (optional)
          </Label>
          <ComposeInput
            id={fieldId('cc')}
            inputMode="email"
            autoComplete="off"
            placeholder="Separate several addresses with commas"
            value={ccEmails}
            aria-invalid={!ccValid || undefined}
            onChange={(event) => setCcEmails(event.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor={fieldId('reply')} className="text-xs text-muted-foreground">
          Message · PO PDF attached
        </Label>
        <ComposeTextarea
          id={fieldId('reply')}
          rows={5}
          value={replyBody}
          onChange={(event) => setReplyBody(event.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <LoadingButton
          size="sm"
          loading={redirect.isPending}
          loadingText="Sending..."
          disabled={!emailValid || !ccValid || !replyBody.trim() || pending}
          onClick={() =>
            redirect.mutate(
              {
                actionId: action.id,
                bodyText: replyBody,
                resolutionType: 'redirect_contact',
                recipientEmail: recipientEmail.trim(),
                recipientName: recipientName.trim() || undefined,
                recipients: cc.length ? { cc_emails: cc } : undefined,
              },
              handlers(`PO sent to ${recipientEmail.trim()}`, 'PO not sent'),
            )
          }
        >
          Send PO
        </LoadingButton>
        {back}
      </div>
    </div>
  )
}
